import React from 'react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import media from 'styled-media-query';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import { Box, Title, Text, Link, Divider } from '@firsttable/web-components/atoms';
import IconLinkedin from '@firsttable/web-components/atoms/Icon/IconLinkedin';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';

const ImageWrap = styled(Box)`
  display: inline-block;
  vertical-align: top;
  position: relative;
  max-width: 272px;
  width: 100%;
  flex-shrink: 0;
  align-self: flex-start;
`;
const Linkedin = styled(Box)`
  position: absolute;
`;
const StyledImg = styled(Box)`
  aspect-ratio: 1 / 1.32;
`;

const Holder = styled(Box)`
  position: absolute;
  top: 21px;
  right: -21px;
  width: 21px;
  bottom: 0;
  background: linear-gradient(180deg, #f6f8f9 27.52%, #fff 100%);
  ${media.lessThan(bp.s)`
    top: 11px;
    right: -11px;
    width: 11px;
  `};
`;

const MemberImg = ({ image, name }) => (
  <>
    {image && (
      <StyledImg bg="grayscale.300">
        <Image
          src={image}
          alt={name}
          width={272}
          height={360}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          sizes={`(max-width: ${bp.s}) 50vw, 272px`}
          quality={70}
        />
      </StyledImg>
    )}
  </>
);

MemberImg.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
};

const MemberCard = ({
  data = null,
  image = null,
  name = null,
  post = null,
  linkedin = null,
  to = null,
  children = null,
  NavLinkComponent,
  ...rest
}: {
  data?: any;
  image?: any;
  name?: string | null;
  post?: string;
  linkedin?: string;
  to?: any;
  children?: any;
  NavLinkComponent: any;
}) => (
  <Box maxWidth={272} mx="auto" {...rest}>
    <Box display={['block', 'flex']}>
      <ImageWrap mb="24px">
        {to && !children ? (
          <TrackProvider link_name="team member">
            <Link NavComponent={NavLinkComponent} to={to}>
              <MemberImg image={image} />
            </Link>
          </TrackProvider>
        ) : (
          <MemberImg image={image} name={name} />
        )}
        {linkedin && (
          <Linkedin bottom={[0, '-24px']} right={[0, '24px']} colors="card.s">
            <TrackProvider link_name="team member">
              <Link href={linkedin}>
                <IconLinkedin />
              </Link>
            </TrackProvider>
          </Linkedin>
        )}
        <Holder />
      </ImageWrap>
      {children && (
        <Box flex={1} ml={[0, '70px']}>
          <Title as="h3" size="h6" mb={['24px', '32px']}>
            {post}
          </Title>
          <Divider mb={['24px', '32px']} />
          {children}
        </Box>
      )}
    </Box>
    {!children && (
      <>
        <Title as="h3" size="h6" mb="8px">
          {to ? (
            <TrackProvider link_name="team member">
              <Link NavComponent={NavLinkComponent} to={to}>
                {name}
              </Link>
            </TrackProvider>
          ) : (
            <>{name}</>
          )}
        </Title>
        <Text m={0}>{post}</Text>
      </>
    )}
  </Box>
);

MemberCard.propTypes = {
  image: PropTypes.string,
  data: PropTypes.object,
  name: PropTypes.string,
  post: PropTypes.string,
  linkedin: PropTypes.string,
  to: PropTypes.string,
  children: PropTypes.node,
  NavLinkComponent: PropTypes.any.isRequired,
};
export default MemberCard;
